import I18n from 'i18n-js';
import {
  CASH_BY_RECIPIENT,
  CASH_BY_SENDER,
  PAYMENT_TYPE_GRABPAY,
  POSTPAID_BILLING_TYPE,
} from 'constants/booking-constants';
import _get from 'lodash/get';
import _sumBy from 'lodash/sumBy';
import calcPrice from 'common/utils/calc-price';
import formatCurrency from 'common/utils/format-currency';
import paymentIcon from 'components/booking/payment-field/payment-icon';
import { getDeliveryFinalDropOffs } from 'helper/anytime-booking';

export const CashPaymentMethods = [
  {
    type: CASH_BY_SENDER,
    paymentTypeID: CASH_BY_SENDER,
  },
  {
    type: CASH_BY_RECIPIENT,
    paymentTypeID: CASH_BY_RECIPIENT,
  },
];

export const generatePaymentMethodToRender = (
  paymentMethod,
  delivery,
  city,
  options = {
    isCashlessPaymentDisabled: false,
    isCashPaymentDisabled: false,
    totalAmount: undefined,
    onRetryFailedPayments: undefined,
    onReloadPaymentMethods: undefined,
  },
) => {
  const {
    isCashlessPaymentDisabled = false,
    isCashPaymentDisabled = false,
    totalAmount = undefined,
    onRetryFailedPayments,
    onReloadPaymentMethods,
  } = options;

  const serviceAllowCash = _get(delivery, 'config.paymentMethods', [
    'CASH',
  ]).includes('CASH');
  const serviceAllowCard = _get(delivery, 'config.paymentMethods', [
    'CASH',
  ]).includes('CARD');

  const isSameDayService =
    _get(delivery, 'detail.configuration.serviceType') === 'SAME_DAY';
  const dropOffs = getDeliveryFinalDropOffs(_get(delivery, 'detail'));
  const isMSD = _get(dropOffs, 'length', 0) > 1;

  const cashlessPayment = !(
    paymentMethod.paymentTypeID === CASH_BY_SENDER ||
    paymentMethod.paymentTypeID === CASH_BY_RECIPIENT
  );

  const paymentMethodToRender = {
    type: paymentMethod.type,
    paymentTypeID: paymentMethod.paymentTypeID,
    isPrimary: !!paymentMethod.isPrimary,
    icon: '',
    disabled: !paymentMethod.enabled,
    disabledReason: undefined,
    ctaText: undefined,
    ctaFunc: undefined,
    cashlessPayment,
  };

  if (cashlessPayment && isCashlessPaymentDisabled) {
    paymentMethodToRender.disabled = true;
    paymentMethodToRender.disabledReason = I18n.t(
      'v2.failedPayments.bookingPopoverText',
    );
    paymentMethodToRender.ctaText = I18n.t(
      'v2.failedPayments.bookingPopoverButton',
    );
    paymentMethodToRender.ctaFunc = onRetryFailedPayments;
  }

  switch (paymentMethod.type) {
    case PAYMENT_TYPE_GRABPAY: {
      const isOVO = _get(city, 'countryCode') === 'ID';
      const isMOCA = _get(city, 'countryCode') === 'VN';

      if (isOVO) {
        paymentMethodToRender.icon = paymentIcon.OVO;
      } else if (isMOCA) {
        paymentMethodToRender.icon = paymentIcon.MOCA;
      } else {
        paymentMethodToRender.icon = paymentIcon.grabpay;
      }

      const balance = _get(paymentMethod, 'payload.Balance');
      paymentMethodToRender.displayName = formatCurrency({
        code: _get(paymentMethod, 'payload.Currency'),
        amount: balance,
      });

      if (!paymentMethodToRender.disabled) {
        if (totalAmount === undefined || balance < totalAmount) {
          paymentMethodToRender.disabled = true;
          paymentMethodToRender.disabledReason = isOVO
            ? I18n.t('ovo.insufficientBalance.popOverText')
            : I18n.t('v2.failedPayments.grabPay.popOverText');
          paymentMethodToRender.ctaText = isOVO
            ? I18n.t('ovo.insufficientBalance.bookingPopoverButton')
            : I18n.t('v2.failedPayments.grabPay.bookingPopoverButton');
          paymentMethodToRender.ctaFunc = onReloadPaymentMethods;
        }
      }

      break;
    }

    case POSTPAID_BILLING_TYPE: {
      paymentMethodToRender.icon = paymentIcon.postpaidBilling;
      paymentMethodToRender.displayName = I18n.t(
        'booking.paymentMethod.corpBilling',
      );
      break;
    }

    case CASH_BY_SENDER: {
      paymentMethodToRender.icon = paymentIcon.CASH;
      paymentMethodToRender.displayName = I18n.t(
        'booking.paymentMethod.cashBySender',
      );
      paymentMethodToRender.disabled =
        !serviceAllowCash || (!isSameDayService && isCashPaymentDisabled);
      break;
    }

    case CASH_BY_RECIPIENT: {
      paymentMethodToRender.icon = paymentIcon.CASH;
      paymentMethodToRender.displayName = I18n.t(
        'booking.paymentMethod.cashByRecipient',
      );
      paymentMethodToRender.disabled =
        !serviceAllowCash ||
        (!isSameDayService && isCashPaymentDisabled) ||
        isMSD;
      break;
    }

    default: {
      // credit cards
      const { type, refNumber } = paymentMethod;
      const accountIcon = _get(paymentMethod, 'payload.AccountIcon');
      paymentMethodToRender.icon =
        accountIcon || paymentIcon[type] || paymentIcon.generic;
      paymentMethodToRender.displayName = refNumber;
      paymentMethodToRender.disabled =
        paymentMethodToRender.disabled || !serviceAllowCard;
      break;
    }
  }

  return paymentMethodToRender;
};

// Map a PaySDK-emitted id onto a legacy-known paymentTypeID (a `paymentMethods`
// entry or cash sentinel) so the BFF quote call stays valid. Falls back to the
// auto-select pick when the id isn't recognized (e.g. local/mock PaySDK).
export const resolvePaySdkPaymentTypeID = ({
  paymentID,
  paymentMethods = [],
  deliveries = [],
  city = {},
  isCashlessPaymentDisabled = false,
  isCashPaymentDisabled = false,
}) => {
  if (deliveries.length === 0) return '';

  const idStr = paymentID != null ? String(paymentID) : '';
  const accountPaymentMethods = [...paymentMethods, ...CashPaymentMethods];
  const currency = _get(deliveries[0], 'quote.currency');
  const totalFinalFare = _sumBy(deliveries, delivery =>
    _get(delivery, 'quote.finalFare.lowerBound', 0),
  );
  const options = {
    totalAmount: calcPrice(totalFinalFare, currency),
    totalDeliveries: deliveries.length,
    isCashlessPaymentDisabled,
    isCashPaymentDisabled,
  };

  const rendered = accountPaymentMethods.map(pm =>
    generatePaymentMethodToRender(pm, deliveries[0], city, options),
  );

  // if a method is disabled for any delivery in the batch, treat it as disabled for the batch.
  deliveries.slice(1).forEach(delivery => {
    accountPaymentMethods.forEach((pm, index) => {
      const overwrite = generatePaymentMethodToRender(
        pm,
        delivery,
        city,
        options,
      );
      if (!rendered[index].disabled && overwrite.disabled) {
        rendered[index].disabled = overwrite.disabled;
      }
    });
  });

  const exact = rendered.find(pm => pm.paymentTypeID === idStr && !pm.disabled);
  if (exact) return exact.paymentTypeID;

  // PaySDK uses "0" as the cash sentinel; map to CASH_BY_SENDER if cash is available.
  if (idStr === '0') {
    const cashSender = rendered.find(
      pm => pm.paymentTypeID === CASH_BY_SENDER && !pm.disabled,
    );
    return cashSender ? cashSender.paymentTypeID : '';
  }

  // Only passthrough IDs completely absent from rendered — those are wallet methods
  // (OVO, GrabPay) not in paxFetchPaymentMethodsEpt. Known-but-disabled IDs fall
  // through to auto-select so we don't persist an unusable method.
  const known = rendered.find(pm => pm.paymentTypeID === idStr);
  if (idStr && !known) return idStr;

  // No ID provided — auto-select the primary/first non-disabled method.
  const fallback =
    rendered.find(pm => pm.isPrimary && !pm.disabled) ||
    rendered.find(pm => !pm.disabled);
  return fallback ? fallback.paymentTypeID : '';
};
